import { useEffect, useRef } from "react";

/**
 * Custom hook to handle map container resize events.
 * When the map container gets resized, the map would not automatically follow (eg with drawer collapse/expand)
 * We use a debounce to prevent the map to flash white during transitions.
 * Yet the timeout is kept short so it rerenders a few times in the transition choppy resizes.
 * @param mapRef - Reference to the mapbox map instance.
 */
export function useMapboxResize(mapRef: React.RefObject<mapboxgl.Map | null>) {
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!mapRef?.current) return;

        const resizeObserver = new ResizeObserver(() => {
            // Debounce resize calls
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            resizeTimeoutRef.current = setTimeout(() => {
                mapRef.current?.resize();
            }, 10);
        });

        const container = mapRef.current.getContainer();
        if (container) {
            resizeObserver.observe(container);
        }

        return () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }
            resizeObserver.disconnect();
        };
    }, [mapRef]);
}
